<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click.self="handleClose"
  >
    <div class="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
      <div class="flex items-center justify-between mb-1">
        <h3 class="font-semibold text-gray-800 text-lg">Change Password</h3>
        <button @click="handleClose" class="text-gray-400 hover:text-gray-600" aria-label="Close">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <p class="text-sm text-gray-500 mb-5">You'll be signed out once your password is changed, so you can log back in with it.</p>

      <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
        {{ errorMessage }}
      </div>

      <form @submit.prevent="submit" class="space-y-4">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">Current Password</label>
          <div class="relative">
            <input
              :type="showCurrent ? 'text' : 'password'"
              v-model="form.currentPassword"
              autocomplete="current-password"
              class="w-full border border-gray-300 rounded p-2 pr-10 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-custom-blue"
              required
            />
            <button type="button" @click="showCurrent = !showCurrent" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <svg v-if="showCurrent" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18M10.584 10.587a2 2 0 002.828 2.83M9.363 5.365A9.466 9.466 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.965 9.965 0 01-1.63 2.943M6.11 6.11A9.965 9.965 0 002.458 12c1.02 3.24 3.42 5.84 6.49 6.686" />
              </svg>
            </button>
          </div>
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">New Password</label>
          <div class="relative">
            <input
              :type="showNew ? 'text' : 'password'"
              v-model="form.newPassword"
              autocomplete="new-password"
              class="w-full border border-gray-300 rounded p-2 pr-10 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-custom-blue"
              required
              minlength="8"
            />
            <button type="button" @click="showNew = !showNew" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <svg v-if="showNew" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18M10.584 10.587a2 2 0 002.828 2.83M9.363 5.365A9.466 9.466 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.965 9.965 0 01-1.63 2.943M6.11 6.11A9.965 9.965 0 002.458 12c1.02 3.24 3.42 5.84 6.49 6.686" />
              </svg>
            </button>
          </div>
          <p class="text-xs text-gray-400 mt-1">At least 8 characters.</p>
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">Confirm New Password</label>
          <div class="relative">
            <input
              :type="showConfirm ? 'text' : 'password'"
              v-model="form.confirmPassword"
              autocomplete="new-password"
              class="w-full border border-gray-300 rounded p-2 pr-10 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-custom-blue"
              required
            />
            <button type="button" @click="showConfirm = !showConfirm" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <svg v-if="showConfirm" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18M10.584 10.587a2 2 0 002.828 2.83M9.363 5.365A9.466 9.466 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.965 9.965 0 01-1.63 2.943M6.11 6.11A9.965 9.965 0 002.458 12c1.02 3.24 3.42 5.84 6.49 6.686" />
              </svg>
            </button>
          </div>
          <p v-if="form.confirmPassword && form.newPassword !== form.confirmPassword" class="text-xs text-red-500 mt-1">
            Passwords do not match.
          </p>
        </div>

        <div class="flex justify-end gap-3 pt-2">
          <button type="button" @click="handleClose" class="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="submit"
            :disabled="submitting || !canSubmit"
            class="px-4 py-2 text-sm rounded bg-custom-blue text-white hover:opacity-90 disabled:opacity-50"
          >
            {{ submitting ? "Changing..." : "Change Password" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";

export default {
  name: "ChangePasswordModal",
  props: {
    show: { type: Boolean, required: true },
  },
  emits: ["close"],
  data() {
    return {
      form: { currentPassword: "", newPassword: "", confirmPassword: "" },
      showCurrent: false,
      showNew: false,
      showConfirm: false,
      submitting: false,
      errorMessage: "",
    };
  },
  computed: {
    canSubmit() {
      return (
        this.form.currentPassword.length > 0 &&
        this.form.newPassword.length >= 8 &&
        this.form.newPassword === this.form.confirmPassword
      );
    },
  },
  watch: {
    show(val) {
      if (val) this.resetForm();
    },
  },
  methods: {
    resetForm() {
      this.form = { currentPassword: "", newPassword: "", confirmPassword: "" };
      this.showCurrent = this.showNew = this.showConfirm = false;
      this.errorMessage = "";
      this.submitting = false;
    },
    handleClose() {
      if (this.submitting) return;
      this.$emit("close");
    },
    async submit() {
      if (!this.canSubmit) return;
      this.errorMessage = "";
      this.submitting = true;
      try {
        const response = await apiClient.changePassword({
          current_password: this.form.currentPassword,
          new_password: this.form.newPassword,
          new_password_confirmation: this.form.confirmPassword,
        });

        if (response.success) {
          this.$toast?.success?.("Password changed. Please log in again.");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          this.$router.push("/login");
        } else {
          this.errorMessage = response.message || "Failed to change password.";
        }
      } catch (error) {
        this.errorMessage = error?.message || "Failed to change password.";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
